import { FC, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import { ScreenState } from '@enums/index';
import { useAppDispatch, useAppSelector } from '@hooks/store';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { Box, Grid } from '@mui/material';
import PageHeader from '@components/pageHeader';
import PortfolioSelection from '@components/portfolioSelection';
import TransactionsDataGrid from './TransactionsDataGrid';
import { useWindow } from '@src/app/hooks/useWindow';
import TransactionCard from './TransactionCard';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { getTransactionsAsync } from '@src/app/store/reducers/transactions/thunks';
import { selectTransactions } from '@src/app/store/reducers/transactions/selectors';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';
import { setTransactionDataRange } from '@src/app/store/reducers/transactions';

const Transactions: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const [currentPageSize, setCurrentPageSize] = useState<number>(25);

  const { resizing } = useWindow();
  const { transitioningMenu } = useAppContext();

  const { data, state, dateRange, filteredIds, filteredItem } = useAppSelector(selectTransactions);
  const { portfoliosSelected, portfolioInfoState } = useAppSelector(selectPortfolios);

  const updateDateRange = (dates: DateRange) => {
    dispatch(setTransactionDataRange(dates));
  };

  const fetchTransactionsAsync = async () => {
    await dispatch(getTransactionsAsync({ fromDate: dateRange.dateFrom, toDate: dateRange.dateTo, clientPortfolios: portfoliosSelected }));
  };

  useEffect(() => {
    fetchTransactionsAsync();
  }, [portfoliosSelected, dateRange]);

  const RenderDatatable: FC = (): JSX.Element => {
    if (resizing || transitioningMenu) {
      return <></>;
    }
    const isLoading = portfolioInfoState === ScreenState.Loading || state === ScreenState.Loading;
    return (
      <TransactionsDataGrid
        clearFilteringButton
        isLoading={isLoading}
        data={data?.transactions ?? []}
        filterIds={filteredIds}
        filteredItem={filteredItem}
        currentPageSize={currentPageSize}
        setCurrentPageSize={setCurrentPageSize}
      />
    );
  };

  return (
    <Container>
      <Box display="flex" flexDirection="column" mb={6} gap={4}>
        <PageHeader title="Transactions" icon={AttachMoneyOutlinedIcon}>
          <PortfolioSelection allowCombining={false} disabled={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading} />
        </PageHeader>
        <Grid container direction="column" spacing={4}>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker dateRange={dateRange} setDateRange={updateDateRange} />
            </LocalizationProvider>
          </Grid>
          <Grid item container spacing={2}>
            <TransactionCard
              name="Contributions"
              filterIds={data?.portfolioTransactionsInfo?.contributionsTransactions ?? []}
              value={data?.portfolioTransactionsInfo?.contributions ?? 0}
              filterValue="Contribution"
            />
            <TransactionCard
              name="Income"
              filterIds={data?.portfolioTransactionsInfo?.incomeTransactions ?? []}
              value={data?.portfolioTransactionsInfo?.income ?? 0}
              filterValue="Income"
            />
            <TransactionCard
              name="Withdrawals"
              filterIds={data?.portfolioTransactionsInfo?.withdrawalsTransactions ?? []}
              value={data?.portfolioTransactionsInfo?.withdrawals ?? 0}
              filterValue="Withdrawal"
            />
            <TransactionCard
              name="Expenses"
              filterIds={data?.portfolioTransactionsInfo?.expensesTransactions ?? []}
              value={data?.portfolioTransactionsInfo?.expenses ?? 0}
              filterValue="Expense"
            />
            <TransactionCard
              name="Buys"
              filterIds={data?.portfolioTransactionsInfo?.buysTransactions ?? []}
              value={data?.portfolioTransactionsInfo?.buys ?? 0}
              filterValue="Buy"
            />
            <TransactionCard
              name="Sells"
              filterIds={data?.portfolioTransactionsInfo?.sellsTransactions ?? []}
              value={data?.portfolioTransactionsInfo?.sells ?? 0}
              filterValue="Sell"
            />
          </Grid>
          <Grid item>
            <RenderDatatable />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Transactions;
